import type { ReactNode } from "react";
import { toast as sonner } from "sonner";
import { Toaster } from "@/components/ui/sonner";

/**
 * The dashboard's status vocabulary, not sonner's. Call sites say what a
 * message *means* ("danger") and this module decides how the spec renders it,
 * so a change of toast library never reaches a page.
 */
export type ToastTone = "success" | "warning" | "danger" | "info" | "neutral";

type PushToast = (message: string, tone?: ToastTone) => void;

/** Spec page 03: a confirmation is read in a glance, then it leaves. */
const DISMISS_AFTER_MS = 4000;

const push: PushToast = (message, tone = "success") => {
  switch (tone) {
    case "danger":
      sonner.error(message);
      break;
    case "warning":
      sonner.warning(message);
      break;
    case "info":
      sonner.info(message);
      break;
    case "neutral":
      sonner(message);
      break;
    default:
      sonner.success(message);
  }
};

/**
 * Module-level and therefore referentially stable: pages keep `toast` in
 * effect and callback dependency lists, and a new identity per render would
 * re-run every one of them.
 */
export function useToast(): PushToast {
  return push;
}

/**
 * Mounted above the router in `App.tsx` so a toast raised by a mutation
 * survives the navigation that mutation triggers. sonner owns the queue,
 * timers and focus handling; this only pins the spec's appearance onto it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        position="top-right"
        duration={DISMISS_AFTER_MS}
        closeButton
        // No dark theme exists in the token set, so following the OS would only
        // ever produce a palette the spec does not define.
        theme="light"
        // The spec's toast is the navy chip, not a white card: it has to read
        // as an overlay on top of a page that is already white.
        style={
          {
            "--normal-bg": "var(--navy)",
            "--normal-text": "#ffffff",
            "--normal-border": "var(--navy)",
            "--border-radius": "var(--radius-control)",
          } as React.CSSProperties
        }
        toastOptions={{
          classNames: {
            toast: "font-sans text-[13px] shadow-[0_16px_40px_-16px_rgba(16,24,40,0.55)]",
            // Colour carries the status and nothing else, exactly as on the
            // rest of the console: the chip stays navy, the icon changes.
            success: "[&_[data-icon]]:text-success",
            warning: "[&_[data-icon]]:text-warning",
            error: "[&_[data-icon]]:text-danger",
            info: "[&_[data-icon]]:text-brand",
            closeButton:
              "border-white/20! bg-navy! text-white! hover:bg-white/15! hover:border-white/40!",
          },
        }}
      />
    </>
  );
}
